/**
 * ThriftLinkButton Component
 * Compact button that shows THRIFT link status via color and handles link/unlink.
 * Designed to sit in the Account page status bar alongside other action buttons.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { ThriftLinkService, ThriftLinkStatus } from '../services/ThriftLinkService';
import './ThriftLinkCard.css';

interface ThriftLinkCardProps {
  onLinkChange?: (linked: boolean) => void;
}

function ThriftLinkCard({ onLinkChange }: ThriftLinkCardProps) {
  const [linkService] = useState(() => new ThriftLinkService());
  const [status, setStatus] = useState<ThriftLinkStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLinking, setIsLinking] = useState(false);
  const [isUnlinking, setIsUnlinking] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    loadStatus();
    return () => { mountedRef.current = false; };
  }, []);

  const loadStatus = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await linkService.getLinkStatus();
      if (!mountedRef.current) return;
      setStatus(data);
      onLinkChange?.(data.linked);
    } catch {
      if (!mountedRef.current) return;
      // On error, default to unlinked — don't show error in the button
      setStatus({ linked: false });
    } finally {
      if (mountedRef.current) setIsLoading(false);
    }
  }, [linkService, onLinkChange]);

  const handleClick = async () => {
    if (status?.linked) {
      // Unlink flow
      const confirmed = window.confirm(
        'Unlink your THRIFT account? AI features on THRIFT will be disabled until you re-link.'
      );
      if (!confirmed) return;

      setIsUnlinking(true);
      try {
        await linkService.unlinkThrift();
        if (!mountedRef.current) return;
        setStatus({ linked: false });
        onLinkChange?.(false);
      } catch {
        if (!mountedRef.current) return;
        alert('Failed to unlink. Please try again.');
      } finally {
        if (mountedRef.current) setIsUnlinking(false);
      }
    } else {
      // Link flow
      setIsLinking(true);
      try {
        const response = await linkService.initiateLinking();
        window.location.href = response.redirect_url;
      } catch {
        if (!mountedRef.current) return;
        alert('Failed to start linking. Please try again.');
        setIsLinking(false);
      }
    }
  };

  const isLinked = status?.linked ?? false;
  const isBusy = isLinking || isUnlinking;

  let label: string;
  if (isLoading) {
    label = 'THRIFT...';
  } else if (isUnlinking) {
    label = 'Unlinking...';
  } else if (isLinking) {
    label = 'Linking...';
  } else if (isLinked) {
    label = 'THRIFT Linked';
  } else {
    label = 'Link THRIFT';
  }

  const title = isLinked
    ? `Linked to ${status?.thrift_display_name || 'THRIFT'}${status?.thrift_email ? ` (${status.thrift_email})` : ''} — click to unlink`
    : 'Link your THRIFT account to use AI features across both platforms';

  return (
    <button
      className={`status-thrift-btn ${isLinked ? 'status-thrift-btn--linked' : ''}`}
      onClick={handleClick}
      disabled={isLoading || isBusy}
      title={title}
      aria-label={title}
    >
      <span className={`status-thrift-dot ${isLinked ? 'status-thrift-dot--linked' : ''}`} />
      {label}
    </button>
  );
}

export default ThriftLinkCard;
